import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdAttachFile } from 'react-icons/md';
import {
  useAddAIChatDocument,
  useAIChatDocuments,
  useCreateAIChatSession,
  useDeleteAIChatDocument,
  useRestoredAIChatSession,
  useSendAIChatMessageStream,
} from '@/lib/sdk/ai-chat/queries';
import { extractAIError, isChatEndedError } from '@/lib/sdk/ai-chat/types';
import type { AIChatDocument, AIChatMessageMetadata } from '@/lib/sdk/ai-chat/types';
import { Modal } from '@/design-system/components';
import { useCanBuildWithAI } from '@/modules/workflows/ai/useCanBuildWithAI';
import { linkCitations } from '../citations';
import { GuideMarkdown } from './GuideMarkdown';
import { PlanCard } from './PlanCard';

const solveSchema = z.object({
  message: z.string().trim().min(1, "Describe the client's requirement").max(8000, 'Keep it under 8000 characters'),
});

type SolveFormData = z.infer<typeof solveSchema>;

/** What the server converts: PDFs, photos of paper forms, Excel and Word */
const documentTypes = '.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.docx';
const maxDocumentBytes = 10 * 1024 * 1024;
const maxDocuments = 3;

interface Exchange {
  role: 'user' | 'assistant';
  content: string;
  metadata?: AIChatMessageMetadata;
}

interface SolvePanelProps {
  titles: Record<string, string>;
  onOpenEntry: (entryId: string) => void;
  /** The session in the URL (?solve=) when the page opens, restored from the server */
  initialSessionId: string | null;
  /** Puts a new session in the URL, so Back from Build or a reload returns to it */
  onSessionCreated: (sessionId: string) => void;
}

/** The Build page reads the brief from navigation state and opens Build with it */
export interface BuildBriefState {
  aiBrief: string;
}

/**
 * Solve mode: a client requirement in plain words becomes a configuration plan, after a
 * few discovery questions. Follow-up messages refine the plan, and Build this hands a
 * workflow to Build mode. The session is created on the first message.
 */
export function SolvePanel({ titles, onOpenEntry, initialSessionId, onSessionCreated }: SolvePanelProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const canBuild = useCanBuildWithAI();
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  // Only the session the page opened with is fetched; a new one is already on screen
  const [restoreId] = useState(initialSessionId ?? '');
  const restored = useRestoredAIChatSession(restoreId, (messages) =>
    setExchanges(
      messages
        .filter((message) => message.role !== 'system')
        .map((message) => ({ role: message.role as Exchange['role'], content: message.content, metadata: message.metadata })),
    ),
    (session) => session.session_type === 'solution'
  );
  const [progress, setProgress] = useState<string | null>(null);
  const createSession = useCreateAIChatSession();
  const sendMessage = useSendAIChatMessageStream();
  const documents = useAIChatDocuments(sessionId);
  const addDocument = useAddAIChatDocument();
  const deleteDocument = useDeleteAIChatDocument();
  const fileInput = useRef<HTMLInputElement>(null);
  const [previewDocument, setPreviewDocument] = useState<AIChatDocument | null>(null);
  const userBg = useColorModeValue('brand.50', 'gray.700');
  const isWorking = createSession.isPending || sendMessage.isPending || addDocument.isPending || restored.isLoading || restored.isResuming;

  useEffect(() => {
    if (restored.problem) {
      toast({ status: 'error', title: restored.problem });
    }
  }, [restored.problem, toast]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<SolveFormData>({ resolver: zodResolver(solveSchema), defaultValues: { message: '' } });

  /** A new session can't see an ended chat's history, so it leaves the screen too */
  const dropEndedChat = () => {
    if (restored.isGone && sessionId === restoreId) {
      setExchanges([]);
      toast({ status: 'info', title: 'That chat has ended, so this starts a new one' });
    }
  };

  /** The session to use, created on the first message or document */
  const ensureSession = async (): Promise<string> => {
    // A session that could not be restored (deleted or expired) is replaced
    const current = restored.isGone && sessionId === restoreId ? null : sessionId;
    if (current) {
      return current;
    }
    const created = (await createSession.mutateAsync({ session_type: 'solution' })).id;
    setSessionId(created);
    onSessionCreated(created);
    return created;
  };

  const attach = async (file: File) => {
    if (file.size > maxDocumentBytes) {
      toast({ status: 'error', title: 'The file is over 10 MB' });
      return;
    }
    dropEndedChat();
    try {
      await addDocument.mutateAsync({ sessionId: await ensureSession(), file });
    } catch (error) {
      toast({
        status: 'error',
        title: `${file.name} could not be read`,
        description: extractAIError(error)?.message ?? (error instanceof Error ? error.message : undefined),
      });
    }
  };

  const onSubmit = async ({ message }: SolveFormData) => {
    dropEndedChat();
    setExchanges((previous) => [...previous, { role: 'user', content: message }]);
    reset();
    try {
      const activeSessionId = await ensureSession();
      const response = await sendMessage.mutateAsync({
        sessionId: activeSessionId,
        data: { content: message },
        handlers: { onProgress: setProgress },
      });
      const reply = response.message;
      setExchanges((previous) => [...previous, { role: 'assistant', content: reply?.content ?? '', metadata: reply?.metadata }]);
    } catch (error) {
      // Back in the box, ready to send again
      if (isChatEndedError(error)) {
        setSessionId(null);
        setExchanges([]);
      } else {
        setExchanges((previous) => previous.slice(0, -1));
      }
      setValue('message', message);
      toast({
        status: 'error',
        title: 'The requirement could not be solved',
        description: extractAIError(error)?.message ?? (error instanceof Error ? error.message : undefined),
      });
    } finally {
      setProgress(null);
    }
  };

  const answerWith = (option: string) => {
    const current = getValues('message').trim();
    setValue('message', current ? `${current}\n${option}` : option);
  };

  const build = (brief: string) => {
    const state: BuildBriefState = { aiBrief: brief };
    navigate(`/dashboard/${orgId}/create-workflow-v4?ai=1`, { state });
  };

  return (
    <VStack align="stretch" spacing={3} data-testid="guide-solve-panel">
      <Box>
        <Text fontWeight="semibold">Solve a requirement</Text>
        <Text fontSize="xs" color="text-secondary">
          Describe what the client needs in plain words, and attach the form or spreadsheet they use today. You get a
          configuration plan built from the guide, with what the product can&apos;t do, ready to copy into a proposal or
          build.
        </Text>
      </Box>

      <VStack align="stretch" spacing={3}>
        {exchanges.map((exchange, index) => {
          if (exchange.role === 'user') {
            return (
              <Box key={index} bg={userBg} borderRadius="md" px={3} py={2} alignSelf="flex-end" maxW="80%" data-testid="solve-requirement">
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {exchange.content}
                </Text>
              </Box>
            );
          }
          if (exchange.metadata?.plan) {
            return (
              <PlanCard
                key={index}
                content={exchange.content}
                metadata={exchange.metadata}
                titles={titles}
                onOpenEntry={onOpenEntry}
                onBuild={canBuild ? build : undefined}
              />
            );
          }
          const questions = exchange.metadata?.questions;
          return (
            <Box key={index} data-testid={questions ? 'solve-questions' : 'solve-reply'}>
              {questions ? (
                questions.map((question) => (
                  <Box key={question.text} mb={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      {question.text}
                    </Text>
                    <Wrap spacing={1} mt={1}>
                      {question.options?.map((option) => (
                        <WrapItem key={option}>
                          <Button size="xs" variant="outline" onClick={() => answerWith(option)} data-testid="solve-option">
                            {option}
                          </Button>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                ))
              ) : (
                <GuideMarkdown content={linkCitations(exchange.content, titles)} onOpenEntry={onOpenEntry} fontSize="sm" />
              )}
            </Box>
          );
        })}
        {isWorking && (
          <HStack spacing={2} data-testid="solve-progress">
            <Spinner size="xs" />
            <Text fontSize="xs" color="text-secondary">
              {addDocument.isPending ? `Reading ${addDocument.variables?.file.name ?? 'the document'}…` : (progress ?? (restored.isResuming ? 'Still working…' : 'Designing…'))}
            </Text>
          </HStack>
        )}
      </VStack>

      <form onSubmit={handleSubmit(onSubmit)}>
        {documents.data && documents.data.length > 0 && (
          <Wrap spacing={2} mb={2} data-testid="solve-documents">
            {documents.data.map((document) => (
              <WrapItem key={document.id}>
                <Tag size="md" variant="subtle" colorScheme="brand" data-testid="solve-document">
                  <TagLabel
                    as="button"
                    type="button"
                    onClick={() => setPreviewDocument(document)}
                    cursor="pointer"
                    data-testid="solve-document-open"
                  >
                    {document.filename}
                    {document.truncated ? ' (first part only)' : ''}
                  </TagLabel>
                  <TagCloseButton
                    aria-label={`Remove ${document.filename}`}
                    isDisabled={deleteDocument.isPending || isWorking}
                    onClick={() =>
                      sessionId &&
                      deleteDocument.mutate(
                        { sessionId, documentId: document.id },
                        {
                          onError: (error) =>
                            toast({
                              status: 'error',
                              title: `${document.filename} could not be removed`,
                              description: extractAIError(error)?.message ?? (error instanceof Error ? error.message : undefined),
                            }),
                        }
                      )
                    }
                    data-testid="solve-document-remove"
                  />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
        <FormControl isInvalid={!!errors.message}>
          <Textarea
            size="sm"
            rows={4}
            placeholder={
              exchanges.length === 0
                ? 'e.g. A restaurant chain wants to track the food items and quantity consumed at each outlet'
                : 'Answer the questions, or refine the plan'
            }
            isDisabled={isWorking}
            data-testid="solve-input"
            {...register('message')}
          />
          <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
        </FormControl>
        <HStack spacing={2} mt={2}>
          <Button type="submit" size="sm" isLoading={isWorking} data-testid="solve-send">
            {exchanges.length === 0 ? 'Solve' : 'Send'}
          </Button>
          <Tooltip label={`Attach a PDF, photo, Excel or Word file (up to ${maxDocuments})`}>
            <IconButton
              aria-label="Attach a document"
              icon={<MdAttachFile />}
              size="sm"
              variant="ghost"
              isDisabled={isWorking || (documents.data?.length ?? 0) >= maxDocuments}
              onClick={() => fileInput.current?.click()}
              data-testid="solve-attach"
            />
          </Tooltip>
          <input
            ref={fileInput}
            type="file"
            accept={documentTypes}
            hidden
            data-testid="solve-file-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) {
                void attach(file);
              }
            }}
          />
        </HStack>
      </form>

      <Modal
        isOpen={previewDocument !== null}
        onClose={() => setPreviewDocument(null)}
        title={previewDocument?.filename ?? ''}
        size="xl"
        scrollBehavior="inside"
        testId="solve-document-preview"
      >
        <Text fontSize="xs" color="text-secondary" mb={2}>
          This is what the assistant reads from the file.
          {previewDocument?.truncated ? ' The document was long, so only its first part is used.' : ''}
        </Text>
        <GuideMarkdown content={previewDocument?.markdown ?? ''} onOpenEntry={onOpenEntry} fontSize="sm" />
      </Modal>
    </VStack>
  );
}
